/**
 * Capacidades del track de vídeo: zoom, linterna y enfoque.
 *
 * REGLA DE ORO de este fichero: **nunca llama a `getUserMedia`**. Todo lo que
 * hace son `applyConstraints` sobre un track que ya está vivo. Así el código
 * nuevo no puede reproducir el `NotReadableError` de las tablets Samsung que
 * `cameraManager.ts` costó tanto domar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { logCam, errInfo } from './cameraManager';
import { MAX_DIGITAL_ZOOM } from './capture';

/**
 * Las definiciones DOM de TypeScript no incluyen las capacidades de imagen
 * (son de la especificación `mediacapture-image`, no del núcleo de
 * `getUserMedia`), así que hay que declararlas aquí.
 */
interface ImageCapabilities {
  zoom?: { min: number; max: number; step?: number };
  torch?: boolean;
  focusMode?: string[];
  pointsOfInterest?: unknown;
}

interface ImageConstraintSet {
  zoom?: number;
  torch?: boolean;
  focusMode?: string;
  pointsOfInterest?: { x: number; y: number }[];
}

export interface ZoomState {
  /** ¿Se puede hacer zoom, sea nativo o digital? Siempre `true`. */
  supported: boolean;
  /** `true` si lo hace el dispositivo; `false` si lo emulamos por recorte. */
  isNative: boolean;
  /** Factor actual, siempre en «veces»: 1 = sin zoom. */
  factor: number;
  /** Tope alcanzable en este dispositivo. */
  maxFactor: number;
  setFactor: (f: number) => void;
}

export interface CameraCaps {
  zoom: ZoomState;
  torch: { supported: boolean; on: boolean; toggle: () => void };
  focus: {
    supported: boolean;
    focusAt: (x: number, y: number) => void;
  };
  /**
   * Factor que debe aplicar la interfaz por su cuenta (escalado del vídeo y
   * recorte de la captura). Vale 1 cuando el zoom lo hace el dispositivo.
   */
  digitalZoom: number;
}

const clamp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

/**
 * Convierte el rango que declara el dispositivo en «veces» de zoom.
 *
 * Los dispositivos no se ponen de acuerdo en la unidad: unos publican
 * `{min: 1, max: 8}` y otros `{min: 100, max: 800}` (porcentaje). Dividir por
 * el mínimo funciona en los dos casos, porque el mínimo siempre es «sin zoom».
 */
export const zoomFactorRange = (
  cap: { min: number; max: number } | undefined
): { maxFactor: number } => {
  if (!cap || !cap.min || cap.max <= cap.min) return { maxFactor: 1 };
  return { maxFactor: cap.max / cap.min };
};

/**
 * Modo de enfoque AUTOMÁTICO utilizable, o `undefined` si no hay ninguno.
 *
 * Enfocar donde se toca solo tiene sentido con enfoque automático: la cámara
 * frontal de la tablet de campo declara solo `['manual']`, y ahí el punto de
 * interés no significa nada (se enfoca por distancia). Sin este filtro se
 * pintaba el cuadro de enfoque sobre una cámara incapaz de enfocar — justo lo
 * que se decidió NO hacer con la linterna.
 */
export const pickAutoFocusMode = (
  modes: string[] | undefined
): string | undefined =>
  ['single-shot', 'continuous'].find((m) => modes?.includes(m));

/** Traduce un factor en «veces» al valor en las unidades del dispositivo. */
export const factorToDeviceZoom = (
  factor: number,
  cap: { min: number; max: number; step?: number }
): number => {
  const raw = clamp(cap.min * factor, cap.min, cap.max);
  if (!cap.step || cap.step <= 0) return raw;
  const steps = Math.round((raw - cap.min) / cap.step);
  return clamp(cap.min + steps * cap.step, cap.min, cap.max);
};

export const useCameraCaps = (
  track: MediaStreamTrack | null,
  baseConstraints: MediaTrackConstraints
): CameraCaps => {
  const [caps, setCaps] = useState<ImageCapabilities>({});
  const [factor, setFactorState] = useState(1);
  const [torchOn, setTorchOn] = useState(false);

  // Lo que hay que reaplicar en cada `applyConstraints`: mandar solo el zoom
  // apagaría la linterna, porque `applyConstraints` REEMPLAZA el conjunto de
  // restricciones del track, no lo fusiona.
  const desiredRef = useRef({ factor: 1, torch: false });
  const frameRef = useRef<number | null>(null);

  // Capacidades: se leen UNA vez por track. Cambiar de cámara trae un track
  // nuevo y, con él, un rango de zoom distinto y quizá sin linterna.
  useEffect(() => {
    if (!track) {
      setCaps({});
      return;
    }
    const read = (track.getCapabilities?.() ?? {}) as ImageCapabilities;
    logCam('capacidades del track', {
      zoom: read.zoom,
      torch: read.torch,
      focusMode: read.focusMode,
    });
    setCaps(read);
    // Cámara nueva = se empieza de cero. La frontal no tiene linterna y su
    // rango de zoom no tiene por qué parecerse al de la trasera.
    setFactorState(1);
    setTorchOn(false);
    desiredRef.current = { factor: 1, torch: false };
  }, [track]);

  const nativeZoom = caps.zoom;
  const isNative = !!nativeZoom && nativeZoom.max > nativeZoom.min;
  const maxFactor = isNative
    ? zoomFactorRange(nativeZoom).maxFactor
    : MAX_DIGITAL_ZOOM;
  const torchSupported = caps.torch === true;
  const autoFocusMode = pickAutoFocusMode(caps.focusMode);
  const focusSupported = !!autoFocusMode;

  /**
   * Aplica zoom y linterna JUNTOS y agrupados por fotograma.
   *
   * El pellizco genera decenas de eventos por segundo y la documentación avisa
   * expresamente de aplicar restricciones a alta frecuencia. Aquí no se
   * encadenan promesas: si llega un valor nuevo mientras hay una petición en
   * vuelo, el intermedio se descarta y solo se aplica el último.
   */
  const flush = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      if (!track) return;

      // Cada entrada de `advanced` se evalúa por separado, así que si el
      // dispositivo no puede con una, no tumba la otra.
      const advanced: ImageConstraintSet[] = [];
      if (isNative && nativeZoom) {
        advanced.push({
          zoom: factorToDeviceZoom(desiredRef.current.factor, nativeZoom),
        });
      }
      if (torchSupported) {
        advanced.push({ torch: desiredRef.current.torch });
      }
      if (!advanced.length) return;

      void track
        .applyConstraints({
          ...baseConstraints,
          advanced,
        } as MediaTrackConstraints)
        .catch((err) => {
          // Best-effort a propósito. En particular hay un fallo conocido de
          // Chromium por el que `torch: false` no apaga en algunos
          // dispositivos; el seguro de verdad es que parar el track sí lo
          // apaga, y el gestor para el track al cerrar el modal.
          logCam('applyConstraints (zoom/linterna) falló', errInfo(err));
        });
    });
  }, [track, isNative, nativeZoom, torchSupported, baseConstraints]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    []
  );

  const setFactor = useCallback(
    (f: number) => {
      const next = clamp(f, 1, maxFactor);
      desiredRef.current.factor = next;
      setFactorState(next);
      if (isNative) flush();
    },
    [maxFactor, isNative, flush]
  );

  const toggleTorch = useCallback(() => {
    const next = !desiredRef.current.torch;
    desiredRef.current.torch = next;
    setTorchOn(next);
    flush();
  }, [flush]);

  const focusAt = useCallback(
    (x: number, y: number) => {
      if (!track || !autoFocusMode) return;
      const mode = autoFocusMode;
      void track
        .applyConstraints({
          ...baseConstraints,
          advanced: [
            { focusMode: mode, pointsOfInterest: [{ x, y }] },
          ] as ImageConstraintSet[],
        } as MediaTrackConstraints)
        .catch((err) => logCam('enfoque falló', errInfo(err)));
    },
    [track, autoFocusMode, baseConstraints]
  );

  return useMemo(
    () => ({
      zoom: {
        supported: true,
        isNative,
        factor,
        maxFactor,
        setFactor,
      },
      torch: { supported: torchSupported, on: torchOn, toggle: toggleTorch },
      focus: { supported: focusSupported, focusAt },
      digitalZoom: isNative ? 1 : factor,
    }),
    [
      isNative,
      factor,
      maxFactor,
      setFactor,
      torchSupported,
      torchOn,
      toggleTorch,
      focusSupported,
      focusAt,
    ]
  );
};
